import { useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PaginationComponent from "../../Components/PaginationComponent";
import attendanceService from "../../Services/attendanceService";

const STATUS_STYLES = {
  present: "bg-green-50 text-green-700",
  late: "bg-amber-50 text-amber-700",
  absent: "bg-red-50 text-red-700",
};

const todayString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return [year, month, day].join("-");
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(`${dateString.slice(0, 10)}T00:00:00`);
  return date.toLocaleDateString("en-PK", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (time) => time || "—";

const getStatusClass = (status) =>
  STATUS_STYLES[String(status || "").toLowerCase()] ||
  "bg-gray-100 text-gray-600";

const columnWidths = ["60px", "190px", "100px", "130px", "110px", "110px", "100px"];

const Attendance = () => {
  const [roleFilter, setRoleFilter] = useState("All");
  const [viewMode, setViewMode] = useState("today");
  const [search, setSearch] = useState("");
  const [records, setRecords] = useState([]);
  const [paginatedRecords, setPaginatedRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);

    attendanceService
      .fetchAll({
        role: roleFilter === "All" ? undefined : roleFilter,
        date: viewMode === "today" ? todayString() : undefined,
      })
      .then((response) => {
        if (active) {
          setRecords(Array.isArray(response?.attendance) ? response.attendance : []);
          setCurrentPage(1);
        }
      })
      .catch(() => {
        if (active) {
          setRecords([]);
          setError(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [roleFilter, viewMode]);

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    return records.filter(
      (record) =>
        String(record?.name || "").toLowerCase().includes(query) ||
        String(record?.role || "").toLowerCase().includes(query)
    );
  }, [records, search]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setCurrentPage(1);
  };

  const ColGroup = () => (
    <colgroup>
      {columnWidths.map((width, index) => (
        <col key={index} style={{ width, minWidth: width }} />
      ))}
    </colgroup>
  );

  return (
    <div className="p-3 font-sans sm:p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-gray-500">
            Attendance
          </p>
          <p className="mt-1 text-[11px] text-gray-400">
            {filteredRecords.length} records found
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={roleFilter}
            onChange={(event) => {
              setRoleFilter(event.target.value);
              setCurrentPage(1);
            }}
            className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="All">All Roles</option>
            <option value="Doctor">Doctor</option>
            <option value="Staff">Staff</option>
          </select>

          <div className="flex rounded-lg border border-gray-200 bg-white p-[3px]">
            {["today", "all"].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => handleViewModeChange(mode)}
                className={`rounded-md px-3 py-2.5 text-xs font-medium transition ${
                  viewMode === mode
                    ? "bg-[#2F6DB4] text-white"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                }`}
              >
                {mode === "today" ? "Today" : "All Dates"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative mb-4">
        <SearchIcon
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          style={{ fontSize: 15 }}
        />
        <input
          type="search"
          placeholder="Search by name or role..."
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white py-3 pl-9 pr-4 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-72"
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          Could not load attendance records. Please try again.
        </div>
      )}

      <div className="block sm:hidden">
        {loading ? (
          <div className="py-10 text-center text-xs text-gray-400">Loading...</div>
        ) : paginatedRecords.length === 0 ? (
          <div className="py-10 text-center text-xs text-gray-400">
            No attendance records found
          </div>
        ) : (
          <div className="space-y-2">
            {paginatedRecords.map((record) => (
              <div
                key={record?.id}
                className="rounded-xl border border-gray-200 bg-white px-3 py-3"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold uppercase text-gray-600">
                      {record?.name?.charAt(0) || "A"}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-[12px] font-semibold text-gray-800">
                        {record?.name || "—"}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {record?.role || "—"}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`flex-shrink-0 rounded-lg px-2.5 py-1 text-[10px] font-medium ${getStatusClass(record?.status)}`}
                  >
                    {record?.status || "Unknown"}
                  </span>
                </div>

                <div className="divide-y divide-gray-100">
                  {[
                    { label: "Date", value: formatDate(record?.date) },
                    { label: "Check-in", value: formatTime(record?.check_in_time) },
                    { label: "Check-out", value: formatTime(record?.check_out_time) },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between py-1.5">
                      <span className="w-24 text-[11px] text-gray-400">{label}</span>
                      <span className="ml-2 flex-1 text-right text-[11px] text-gray-700">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="hidden rounded-xl border border-gray-200 overflow-hidden sm:block">
        <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full min-w-[800px] text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <ColGroup />
            <thead>
              <tr>
                {[
                  { label: "Sr No.", align: "left" },
                  { label: "Name", align: "left" },
                  { label: "Role", align: "left" },
                  { label: "Date", align: "center" },
                  { label: "Check-in", align: "center" },
                  { label: "Check-out", align: "center" },
                  { label: "Status", align: "center" },
                ].map(({ label, align }, index) => (
                  <th
                    key={label}
                    className={`sticky top-0 z-20 bg-[#215187] px-4 py-2.5 text-${align} text-[11px] font-medium uppercase text-gray-100 ${
                      index < 6 ? "border-r border-white/10" : ""
                    }`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-xs text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : paginatedRecords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-xs text-gray-400">
                    No attendance records found
                  </td>
                </tr>
              ) : (
                paginatedRecords.map((record, index) => (
                  <tr
                    key={record?.id}
                    className={`border-b border-gray-100 transition-colors hover:bg-green-50 ${
                      index % 2 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="border-r border-gray-100 px-4 py-2.5 text-[12px] font-semibold text-gray-500">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="border-r border-gray-100 px-4 py-2.5">
                      <span className="truncate text-[12px] font-medium text-gray-800">
                        {record?.name || "—"}
                      </span>
                    </td>
                    <td className="truncate border-r border-gray-100 px-4 py-2.5 text-[12px] text-gray-600">
                      {record?.role || "—"}
                    </td>
                    <td className="border-r border-gray-100 px-4 py-2.5 text-center text-[12px] text-gray-600">
                      {formatDate(record?.date)}
                    </td>
                    <td className="border-r border-gray-100 px-4 py-2.5 text-center text-[12px] text-gray-600">
                      {formatTime(record?.check_in_time)}
                    </td>
                    <td className="border-r border-gray-100 px-4 py-2.5 text-center text-[12px] text-gray-600">
                      {formatTime(record?.check_out_time)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span
                        className={`inline-flex rounded-lg px-2.5 py-1 text-[11px] font-medium ${getStatusClass(record?.status)}`}
                      >
                        {record?.status || "Unknown"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={7}
                  className="sticky bottom-0 z-20 border-t border-white/10 bg-[#215187] px-4 py-2.5 text-center text-[12px] font-medium text-gray-100"
                >
                  {filteredRecords.length} total records
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <PaginationComponent
        filteredData={filteredRecords}
        setPaginatedData={setPaginatedRecords}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default Attendance;
